// reports/viewModel.ts
//
// Builds the view-models the on-device report templates are rendered against.
//
// The templates are pure mustache: they can loop and branch, but they cannot
// compute. Every derived value a sheet needs — atlas crop geometry, meter pin
// positions, bounding-box rects, truncation, capitalisation, "is this section
// empty" flags — is resolved here, once, into plain data. That split is what
// lets one template drive both clients: the mobile app runs a Dart twin of this
// file, and the two are pinned to each other by the golden fixtures in
// __tests__/viewModel.golden.test.ts, which the Dart tests replay byte for byte.
//
// Keep this file free of DOM, React, locale lookups and `Date.now()`. Anything
// non-deterministic belongs in the caller, passed in through `meta` — otherwise
// the golden fixtures stop being reproducible and the cross-platform guarantee
// goes with them.

import { cellTrim, hairCoords, makeupCoords, type AtlasCoords } from '../atlas/coords';
import { isSurfacedConcern } from '../utils/conditionLabels';
import type {
  HairAnalysisResult,
  MakeupAnalysisResult,
  PremiumJobCondition,
  PremiumJobResult,
  SkinDetail,
} from '../types/api';
import type { TemplateContext, TemplateValue } from './mustache';

/** Caller-supplied identity for a sheet header. All of it is pre-formatted. */
export interface ReportMeta {
  /** Short display id, e.g. the first 8 characters of the job id, upper-cased. */
  id: string;
  /** Already-localised date string — formatting is the caller's job. */
  date: string;
  /** Full name, or undefined for an anonymous sheet. */
  name?: string;
  /** Document title; shown by the print UI and used as the default filename. */
  title: string;
}

/** Imagery resolved to URLs the template can reference directly. */
export interface ReportImages {
  /** The user's photo. `blob:` on web, `data:` on mobile. */
  portrait?: string;
  /** The packed analysis atlas that every crop is taken from. */
  atlas?: string;
}

const BRAND = 'kivo.skin';

const HAIR_FOOT_NOTE =
  'Personalised hair analysis · generated from your photo. Recommendations are guidance, not professional styling advice.';
const MAKEUP_FOOT_NOTE =
  'Personalised makeup analysis · generated from your photo. Recommendations are guidance, not professional advice.';
const PREMIUM_DISCLAIMER =
  'For cosmetic & educational use only. These insights are AI-generated and non-medical — they reflect appearance-based patterns, not medical conditions. For any concerns about your skin, please consult a qualified professional.';
const PREMIUM_FALLBACK_NARRATIVE =
  'Maintain the skin barrier and integrate targeted antioxidants to support vitality based on detected traits.';

/** An em dash is the sheet's placeholder for a field the analysis left blank. */
const EMPTY = '—';

function orDash(value: string | undefined | null): string {
  return value !== null && value !== undefined && value !== '' ? value : EMPTY;
}

/** Wrap a list so a template can ask both "is there anything?" and "what?". */
function section<T>(items: T[]): { present: boolean; items: T[] } {
  return { present: items.length > 0, items };
}

function meta(m: ReportMeta): TemplateValue {
  return {
    brand: BRAND,
    title: m.title,
    id: m.id,
    date: m.date,
    name: m.name ?? '',
    has_name: !!m.name,
  };
}

/**
 * The CSS that crops one atlas cell into a box.
 *
 * Mirrors atlasCellStyle() in atlas/coords.ts — the same percentage-based
 * background trick — but emits a declaration string, because the template drops
 * it straight into a `style` attribute and mustache cannot build one. The atlas
 * is a single packed image, so cropping by background position keeps every cell
 * on the one decoded bitmap instead of slicing out dozens of copies.
 */
export function atlasCropStyle(atlasUrl: string | undefined, coords: AtlasCoords, cellId: string): string {
  if (!atlasUrl || !(cellId in coords.cells)) return '';
  const cell = coords.cells[cellId];
  const b = cellTrim(coords);
  const x = cell.x + b;
  const y = cell.y + b;
  const w = cell.w - 2 * b;
  const h = cell.h - 2 * b;
  const { w: cw, h: ch } = coords.canvas;
  const posX = cw - w <= 0 ? 0 : (x / (cw - w)) * 100;
  const posY = ch - h <= 0 ? 0 : (y / (ch - h)) * 100;
  return [
    `background-image:url(${atlasUrl})`,
    `background-size:${round(((cw / w) * 100))}% ${round(((ch / h) * 100))}%`,
    `background-position:${round(posX)}% ${round(posY)}%`,
  ].join(';');
}

/** Fixed 4-decimal rounding: float formatting must match Dart's exactly. */
function round(n: number): string {
  return n.toFixed(4).replace(/\.?0+$/, '');
}

/** One captioned atlas crop in a comparison grid. */
function crops(atlas: string | undefined, coords: AtlasCoords, cellIds: string[], captions: string[]): TemplateValue[] {
  return captions.map((caption, i) => ({
    crop_style: atlasCropStyle(atlas, coords, cellIds[i]),
    caption,
  }));
}

/* ─────────────────────────── hair ─────────────────────────── */

export function buildHairViewModel(
  result: HairAnalysisResult,
  m: ReportMeta,
  images: ReportImages = {},
): TemplateContext {
  // The sheet is a fixed A4 page, so every list is capped at what the design was
  // drawn to hold. Overflow would push the footer off the page rather than
  // scroll, so truncation happens here where it is testable.
  const lengths = result.best_lengths.slice(0, 3);
  const best = result.best_styles.map(s => s.name).slice(0, 4);
  const worst = result.less_flattering_styles.map(s => s.name).slice(0, 4);

  return {
    kind: 'hair',
    kicker: 'Hair Analysis',
    title_line1: 'Your hair',
    title_line2: 'report',
    foot_note: HAIR_FOOT_NOTE,
    meta: meta(m),
    portrait: { present: !!images.portrait, src: images.portrait ?? '' },
    stats: [
      { k: 'Hair type', v: orDash(result.hair_type.texture) },
      { k: 'Density', v: orDash(result.hair_type.density) },
      { k: 'Best parting', v: orDash(result.best_parting) },
    ],
    hair_notes: section(result.hair_notes),
    lengths: {
      present: lengths.length > 0,
      items: lengths.map((l, i) => ({
        crop_style: atlasCropStyle(images.atlas, hairCoords, ['A2', 'A3', 'A4'][i]),
        name: l.name,
        sub_label: l.sub_label,
        has_sub: !!l.sub_label,
      })),
    },
    best_colors: section(result.best_hair_colors),
    less_colors: section(result.less_flattering_hair_colors),
    styles: {
      present: best.length > 0 || worst.length > 0,
      best: crops(images.atlas, hairCoords, ['B1', 'B2', 'B3', 'B4'], best),
      worst: crops(images.atlas, hairCoords, ['B6', 'B7', 'B8', 'B9'], worst),
    },
    avoid: section(result.avoid_rules),
  };
}

/* ────────────────────────── makeup ────────────────────────── */

const DEPTH_GRADIENT = '#F4E6D4,#E7C9A9,#C99B6E,#9A6B43';
const CONTRAST_GRADIENT = '#EFE7DC,#C9BFAE,#8A8170,#3a352c';

export function buildMakeupViewModel(
  result: MakeupAnalysisResult,
  m: ReportMeta,
  images: ReportImages = {},
): TemplateContext {
  const eyeBest = result.flattering_eye_looks.slice(0, 5);
  const eyeWorst = result.less_flattering_eye_looks.slice(0, 4);
  const looksBest = result.flattering_makeup_looks.slice(0, 6);
  const looksWorst = result.less_flattering_makeup_looks.slice(0, 4);
  const eyeShades = result.best_eyeshadow_colors.slice(0, 6);

  // `contrast_level.step` is a 1-5 rank; the pin sits at the middle of its band
  // so step 1 and step 5 land inside the track instead of on its end caps.
  const contrastPos = (result.contrast_level.step - 0.5) / 5;

  return {
    kind: 'makeup',
    kicker: 'Makeup Analysis',
    title_line1: 'Your makeup',
    title_line2: 'report',
    foot_note: MAKEUP_FOOT_NOTE,
    meta: meta(m),
    portrait: { present: !!images.portrait, src: images.portrait ?? '' },
    stats: [
      { k: 'Undertone', v: orDash(result.undertone.classification) },
      { k: 'Skin depth', v: orDash(result.skin_depth.level) },
      { k: 'Contrast', v: orDash(result.contrast_level.level) },
    ],
    meters: [
      {
        label: 'Skin depth',
        value: result.skin_depth.level,
        gradient: DEPTH_GRADIENT,
        pin_left: pinLeft(result.skin_depth.indicator_position),
      },
      {
        label: 'Contrast',
        value: result.contrast_level.level,
        gradient: CONTRAST_GRADIENT,
        pin_left: pinLeft(contrastPos),
      },
    ],
    best_features: section(result.best_features),
    foundation: {
      present: result.foundation_guidance.length > 0 || result.best_foundation_shades.length > 0,
      guidance: section(result.foundation_guidance),
      shades: section(result.best_foundation_shades),
      finish: result.best_finish,
      has_finish: !!result.best_finish,
    },
    eyes: {
      present: eyeShades.length > 0,
      shades: eyeShades,
      looks: {
        present: eyeBest.length > 0 || eyeWorst.length > 0,
        best: crops(images.atlas, makeupCoords, ['B1', 'B2', 'B3', 'B4'], eyeBest.slice(0, 4)),
        worst: crops(images.atlas, makeupCoords, ['B6', 'B7', 'B8', 'B9'], eyeWorst),
      },
    },
    blush_lips: {
      present: result.best_blush_shades.length > 0 || result.best_lip_shades.length > 0,
      blush: section(result.best_blush_shades),
      lips: section(result.best_lip_shades),
      finish: result.best_lip_finish,
      has_finish: !!result.best_lip_finish,
    },
    lookbook: {
      present: looksBest.length > 0 || looksWorst.length > 0,
      best: crops(images.atlas, makeupCoords, ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'], looksBest),
      worst: crops(images.atlas, makeupCoords, ['C7', 'C8', 'C9', 'C10'], looksWorst),
    },
  };
}

/** Clamp a 0-1 meter position and offset by half the pin's width. */
function pinLeft(pos: number): string {
  const p = Math.max(0, Math.min(1, Number.isFinite(pos) ? pos : 0));
  return `calc(${round(p * 100)}% - 2px)`;
}

/* ────────────────────────── premium ───────────────────────── */

/**
 * The condition families a sheet can draw, and the palette hex the annotation
 * overlay strokes each with.
 *
 * The icon markup itself lives in the template (shared/condition-icon.html),
 * selected by these flags. It used to be injected from here as a raw string
 * through a triple-brace unescape — nothing untrusted ever reached it, but it
 * was a raw-injection point in a document rendered inside a WebView, so the
 * markup moved to the one file that is allowed to contain markup.
 *
 * The original sheet drew these with the Material Symbols icon webfont, so an
 * export that beat the font load printed rows of empty boxes. Inline SVG in the
 * template has nothing to wait for.
 */
export type ConditionIcon = 'blemish' | 'wrinkle' | 'pigment' | 'eye' | 'star';

interface ConditionStyle { color: string; icon: ConditionIcon }

/** One-hot flags, because a template can branch but cannot compare. */
function iconFlags(icon: ConditionIcon): Record<ConditionIcon, boolean> {
  return {
    blemish: icon === 'blemish',
    wrinkle: icon === 'wrinkle',
    pigment: icon === 'pigment',
    eye: icon === 'eye',
    star: icon === 'star',
  };
}

const COND_BLEMISH: ConditionStyle = { color: '#F4A88E', icon: 'blemish' };
const COND_WRINKLE: ConditionStyle = { color: '#1A6F70', icon: 'wrinkle' };
const COND_PIGMENT: ConditionStyle = { color: '#2AA4A6', icon: 'pigment' };
const COND_EYE: ConditionStyle = { color: '#1F2421', icon: 'eye' };
const COND_DEFAULT: ConditionStyle = { color: '#2AA4A6', icon: 'star' };

/** Map a condition name onto its icon + palette colour. */
export function conditionStyle(name: string): ConditionStyle {
  const n = name.toLowerCase();
  if (n.includes('acne') || n.includes('blemish')) return COND_BLEMISH;
  if (n.includes('wrinkle') || n.includes('line')) return COND_WRINKLE;
  if (n.includes('pigment') || n.includes('spot')) return COND_PIGMENT;
  if (n.includes('circle') || n.includes('eye')) return COND_EYE;
  return COND_DEFAULT;
}

/** Values the model uses to mean "nothing found"; these rows are dropped. */
export function isNoneValue(value: string | undefined | null): boolean {
  if (!value) return true;
  const lower = value.toLowerCase();
  return lower === 'none' || lower === 'not detected' || lower === 'unknown';
}

export function premiumConditions(result: PremiumJobResult): PremiumJobCondition[] {
  return result.detailed_analysis.filter(c => !isNoneValue(c.condition_name) && !isNoneValue(c.severity));
}

export function buildPremiumViewModel(
  result: PremiumJobResult,
  m: ReportMeta,
  images: ReportImages = {},
): TemplateContext {
  const conditions = premiumConditions(result);
  const metrics = result.summary_metrics;
  const recs = result.recommendations;

  // The overlay is drawn in a 1000×1000 viewBox with preserveAspectRatio="none",
  // so model coordinates are used as-is. box_2d is [ymin, xmin, ymax, xmax].
  const boxes: TemplateValue[] = [];
  for (const condition of conditions) {
    const { color } = conditionStyle(condition.condition_name);
    const label = condition.condition_name.replace(/_/g, ' ').slice(0, 12);
    for (const loc of condition.locations) {
      const [ymin, xmin, ymax, xmax] = loc.box_2d;
      boxes.push({
        x: round(xmin),
        y: round(ymin),
        w: round(xmax - xmin),
        h: round(ymax - ymin),
        color,
        label,
        // The caption sits in a pill just above the box; its width is capped so
        // a wide detection cannot stretch the label off the sheet.
        label_y: round(ymin - 18),
        label_w: round(Math.min(100, xmax - xmin + 15)),
        label_x: round(xmin + 6),
        text_y: round(ymin - 6),
      });
    }
  }

  return {
    kind: 'premium',
    meta: {
      title: m.title,
      session_id: m.id,
      date: m.date,
      name: m.name ?? '',
      has_name: !!m.name,
    },
    summary_cards: [
      { label: 'Skin Score', value: `${metrics.overall_score}%`, sub: 'Overall Index', has_swatch: false, swatch: '' },
      { label: 'Skin Type', value: orDash(metrics.skin_type), sub: 'Classification', has_swatch: false, swatch: '' },
      { label: 'Pred. Age', value: `${metrics.age_prediction}y`, sub: 'AI Estimated', has_swatch: false, swatch: '' },
      {
        label: 'Skin Tone',
        value: `Type ${metrics.fitzpatrick_scale}`,
        sub: 'Fitzpatrick',
        has_swatch: !!metrics.skin_tone_hex,
        swatch: metrics.skin_tone_hex,
      },
    ],
    map: {
      has_image: !!images.portrait,
      image_src: images.portrait ?? '',
      boxes,
    },
    avatars: conditions.slice(0, 5).map(c => ({
      color: conditionStyle(c.condition_name).color,
      letter: (c.condition_name.charAt(0) || '?').toUpperCase(),
    })),
    conditions: {
      present: conditions.length > 0,
      items: conditions.map(c => ({
        icon: iconFlags(conditionStyle(c.condition_name).icon),
        color: conditionStyle(c.condition_name).color,
        name: c.condition_name.replace(/_/g, ' '),
        severity: c.severity,
        description: c.description,
      })),
    },
    narrative: recs?.narrative ?? PREMIUM_FALLBACK_NARRATIVE,
    routine_steps: (recs?.routine_steps ?? []).map((label, i) => ({
      num: i + 1,
      label,
      first: i === 0,
    })),
    product_types: recs?.recommended_product_types ?? [],
    disclaimer: PREMIUM_DISCLAIMER,
  };
}

/* ──────────────────────── base (casual) ───────────────────── */

const BASE_SUMMARY_FALLBACK =
  "From your photo, we've built a skin profile and shaped the recommendations below around what we found.";

function capFirst(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Confidence arrives as 0-1; the sheet prints whole percent. */
function confidencePercent(v: number): number {
  const n = Number.isFinite(v) ? v : 0;
  return Math.round(Math.max(0, Math.min(1, n)) * 100);
}

export function buildBaseViewModel(
  skin: SkinDetail,
  m: ReportMeta,
  images: ReportImages = {},
): TemplateContext {
  const concerns = skin.concerns.filter(c => isSurfacedConcern(c.name));

  return {
    kind: 'base',
    meta: {
      title: m.title,
      session_id: m.id,
      date: m.date,
      name: m.name ?? '',
      has_name: !!m.name,
    },
    portrait: { present: !!images.portrait, src: images.portrait ?? '' },
    skin_type: skin.skin_type || 'Unknown',
    summary: skin.general_advice || BASE_SUMMARY_FALLBACK,
    concerns: {
      present: concerns.length > 0,
      items: concerns.map(c => ({
        icon: iconFlags(conditionStyle(c.name).icon),
        name: capFirst(c.name),
        confidence: confidencePercent(c.confidence),
        advice: c.advice || `High-confidence detection indicating the presence of ${c.name}.`,
      })),
    },
    products: section(skin.base_products),
    disclaimer: PREMIUM_DISCLAIMER,
  };
}
